<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import OpusRecorder from 'opus-recorder/dist/recorder.min.js';
import encoderWorkerUrl from 'opus-recorder/dist/encoderWorker.min.js?url';
import AudioRecordingsAPI from 'dashboard/api/audioRecordings';

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const opusRecorder = ref(null);
const isRecording = ref(false);
const isSaving = ref(false);
const files = ref([]);
const loadingFiles = ref(false);
// Map of filename -> object URL (fetched with auth headers)
const blobUrls = ref({});

const loadBlobUrl = async name => {
  if (blobUrls.value[name]) return;
  try {
    const res = await AudioRecordingsAPI.download(name);
    blobUrls.value[name] = URL.createObjectURL(res.data);
  } catch {
    // leave undefined — player will show error state
  }
};

const fetchFiles = async () => {
  loadingFiles.value = true;
  try {
    const res = await AudioRecordingsAPI.list();
    files.value = res.data.files || [];
    // Load blob URLs for all listed files (runs in parallel)
    await Promise.all(files.value.map(f => loadBlobUrl(f.name)));
  } catch {
    files.value = [];
  } finally {
    loadingFiles.value = false;
  }
};

const deleteFile = async name => {
  await AudioRecordingsAPI.remove(name);
  if (blobUrls.value[name]) {
    URL.revokeObjectURL(blobUrls.value[name]);
    delete blobUrls.value[name];
  }
  await fetchFiles();
};

const uploadBlob = async blob => {
  isSaving.value = true;
  try {
    await AudioRecordingsAPI.upload(blob);
    await fetchFiles();
  } finally {
    isSaving.value = false;
  }
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 64,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({ scrollingWaveform: true, renderRecordedAudio: false }),
    ],
  });

  record.value = wavesurfer.value.plugins[0];

  record.value.on('record-end', blob => {
    const audioUrl = URL.createObjectURL(blob);
    wavesurfer.value.load(audioUrl);
  });
};

const initOpusRecorder = () => {
  opusRecorder.value = new OpusRecorder({
    encoderPath: encoderWorkerUrl,
    streamPages: false,
    numberOfChannels: 1,
    encoderSampleRate: 48000,
  });

  opusRecorder.value.ondataavailable = async typedArray => {
    const blob = new Blob([typedArray], { type: 'audio/ogg;codecs=opus' });
    isRecording.value = false;
    await uploadBlob(blob);
  };
};

const startRecording = async () => {
  record.value.startRecording();
  await opusRecorder.value.start();
  isRecording.value = true;
};

const stopRecording = () => {
  if (!isRecording.value) return;
  record.value.stopRecording();
  opusRecorder.value.stop();
};

const formatSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = iso => new Date(iso).toLocaleString();

onMounted(() => {
  initWaveSurfer();
  initOpusRecorder();
  fetchFiles();
});

onUnmounted(() => {
  if (wavesurfer.value) wavesurfer.value.destroy();
  if (opusRecorder.value) opusRecorder.value.close();
  // Revoke all blob URLs to free memory
  Object.values(blobUrls.value).forEach(url => URL.revokeObjectURL(url));
});
</script>

<template>
  <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2">
    <h2 class="text-base font-semibold text-n-slate-12 mb-4">Gravador de Áudio</h2>

    <!-- Waveform -->
    <div ref="waveformContainer" class="w-full rounded-lg bg-n-surface-1 border border-n-weak mb-4" />

    <!-- Controls -->
    <div class="flex items-center gap-3 mb-6">
      <button
        v-if="!isRecording"
        class="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
        @click="startRecording"
      >
        <span class="inline-block w-2.5 h-2.5 rounded-full bg-white" />
        Gravar
      </button>
      <button
        v-else
        class="flex items-center gap-2 rounded-lg bg-n-slate-11 px-5 py-2.5 text-sm font-semibold text-white hover:bg-n-slate-12"
        @click="stopRecording"
      >
        <span class="inline-block w-2.5 h-2.5 bg-white rounded-sm" />
        Parar &amp; Salvar
      </button>
      <span v-if="isRecording" class="flex items-center gap-1.5 text-sm text-red-600 font-medium">
        <span class="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        Gravando...
      </span>
      <span v-if="isSaving" class="text-sm text-n-slate-10">Salvando...</span>
    </div>

    <!-- File list -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-n-slate-12">Arquivos Salvos</h3>
        <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="fetchFiles">
          Atualizar
        </button>
      </div>
      <div v-if="loadingFiles" class="text-sm text-n-slate-10 py-4 text-center">Carregando...</div>
      <div v-else-if="!files.length" class="text-sm text-n-slate-10 py-4 text-center">
        Nenhuma gravação ainda.
      </div>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="f in files"
          :key="f.name"
          class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
        >
          <div class="flex-1 min-w-0">
            <p class="text-xs font-mono text-n-slate-12 truncate">{{ f.name }}</p>
            <p class="text-xs text-n-slate-10">
              {{ formatSize(f.size) }} · {{ formatDate(f.created_at) }}
            </p>
          </div>
          <audio v-if="blobUrls[f.name]" :src="blobUrls[f.name]" controls class="h-8 shrink-0" />
          <button
            class="text-xs text-red-600 hover:text-red-700 font-medium shrink-0"
            @click="deleteFile(f.name)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
